import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Globe, Search, FileText, Bot, Shield, RefreshCw, CheckCircle, AlertTriangle,
  AlertCircle, ChevronDown, ChevronRight, Save, Loader2, ExternalLink, BarChart3,
  MapPin, Sparkles, Eye
} from "lucide-react";

const PUBLIC_PAGES = [
  { slug: "home", label: "Homepage", path: "/" },
  { slug: "verticals", label: "Industries", path: "/verticals" },
  { slug: "our-customers", label: "Happy Customers", path: "/our-customers" },
  { slug: "assessment", label: "Digital Maturity Assessment", path: "/assessment" },
  { slug: "codn-calculator", label: "CODN Calculator", path: "/codn-calculator" },
  { slug: "resources", label: "Resources", path: "/resources" },
  { slug: "schedule-demo", label: "Schedule Demo", path: "/schedule-demo" },
  { slug: "video-testimonials", label: "Video Testimonials", path: "/video-testimonials" },
  { slug: "case-studies", label: "Case Studies", path: "/case-studies" },
];

export default function AdminSEO() {
  const [activeTab, setActiveTab] = useState("health");
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [pageMetas, setPageMetas] = useState({});
  const [robotsTxt, setRobotsTxt] = useState("");
  const [llmsTxt, setLlmsTxt] = useState("");
  const [saving, setSaving] = useState(false);
  const [expandedPage, setExpandedPage] = useState(null);
  // AI Meta tab state
  const [entityMetas, setEntityMetas] = useState({ vertical: [], problem: [] });
  const [aiMetaRunning, setAiMetaRunning] = useState(false);
  const [aiMetaResult, setAiMetaResult] = useState(null);
  const [entityMetaFilter, setEntityMetaFilter] = useState("vertical"); // "vertical" | "problem"
  // Integrations tab state
  const [integrations, setIntegrations] = useState({ ga4_id: "", gsc_verification: "", gtm_id: "", ms_clarity_id: "" });

  useEffect(() => {
    loadHealth();
    loadAllMeta();
    loadConfigs();
    loadEntityMetas();
    loadIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHealth = async () => {
    setHealthLoading(true);
    try {
      const res = await api.get("/seo/health");
      setHealth(res.data);
    } catch (error) {
      console.error("Failed to load SEO health:", error);
    }
    setHealthLoading(false);
  };

  const loadAllMeta = async () => {
    try {
      const res = await api.get("/seo/all-meta");
      const map = {};
      (res.data || []).forEach(m => { map[m.slug] = m; });
      setPageMetas(map);
    } catch (error) {
      console.error("Failed to load page meta:", error);
    }
  };

  const loadConfigs = async () => {
    try {
      const [robotsRes, llmsRes] = await Promise.all([
        api.get("/seo/config/robots"),
        api.get("/seo/config/llms_txt"),
      ]);
      setRobotsTxt(robotsRes.data?.content || "");
      setLlmsTxt(llmsRes.data?.content || "");
    } catch (error) {
      console.error("Failed to load SEO configs:", error);
    }
  };

  const updatePageMeta = (slug, field, value) => {
    setPageMetas(prev => ({
      ...prev,
      [slug]: { ...(prev[slug] || { slug }), [field]: value }
    }));
  };

  const savePageMeta = async (slug) => {
    setSaving(true);
    try {
      const meta = pageMetas[slug] || {};
      await api.put("/seo/page-meta", { slug, title: meta.title || "", description: meta.description || "", keywords: meta.keywords || "", og_image: meta.og_image || "", noindex: meta.noindex || false });
      toast.success(`SEO meta saved for ${slug}`);
    } catch (error) {
      toast.error("Failed to save meta");
    }
    setSaving(false);
  };

  const saveConfig = async (type, content) => {
    setSaving(true);
    try {
      await api.put("/seo/config", { type, content });
      toast.success(`${type === "robots" ? "robots.txt" : "llms.txt"} saved`);
    } catch (error) {
      toast.error("Failed to save config");
    }
    setSaving(false);
  };

  // ===== AI Meta handlers =====
  const loadEntityMetas = async () => {
    try {
      const [vRes, pRes] = await Promise.all([
        api.get("/admin/seo/entity-meta?entity_type=vertical"),
        api.get("/admin/seo/entity-meta?entity_type=problem"),
      ]);
      setEntityMetas({ vertical: vRes.data || [], problem: pRes.data || [] });
    } catch (e) { console.error("Failed to load entity meta", e); }
  };

  const runAIMetaGen = async (entity_type, only_missing) => {
    setAiMetaRunning(true); setAiMetaResult(null);
    try {
      const res = await api.post("/admin/seo/ai-generate-meta", { entity_type, only_missing, limit: 500 });
      setAiMetaResult(res.data);
      toast.success(`AI meta: generated ${res.data.generated}, failed ${res.data.failed}`);
      await Promise.all([loadEntityMetas(), loadHealth()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI meta generation failed");
    }
    setAiMetaRunning(false);
  };

  const runAIPageMetaGen = async (only_missing) => {
    setAiMetaRunning(true);
    try {
      const res = await api.post("/admin/seo/ai-generate-page-meta", { only_missing });
      toast.success(`Static pages: generated ${res.data.generated}, failed ${res.data.failed}`);
      await Promise.all([loadAllMeta(), loadHealth()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Page AI meta generation failed");
    }
    setAiMetaRunning(false);
  };

  const loadIntegrations = async () => {
    try {
      const res = await api.get("/seo/integrations");
      setIntegrations({
        ga4_id: res.data?.ga4_id || "",
        gsc_verification: res.data?.gsc_verification || "",
        gtm_id: res.data?.gtm_id || "",
        ms_clarity_id: res.data?.ms_clarity_id || "",
      });
    } catch (e) { console.error("Failed to load integrations", e); }
  };

  const saveIntegrations = async () => {
    setSaving(true);
    try {
      await api.put("/admin/seo/integrations", integrations);
      toast.success("Integrations saved — applied site-wide");
      // Bust the SEOHead module-level cache so subsequent pages pick up changes without a hard reload
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("seo-integrations-updated"));
      }
      loadHealth();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save integrations"); }
    setSaving(false);
  };

  const tabs = [
    { id: "health", label: "Health Check", icon: BarChart3 },
    { id: "ai-meta", label: "AI Meta", icon: Sparkles },
    { id: "pages", label: "Page Meta", icon: FileText },
    { id: "integrations", label: "Integrations", icon: Globe },
    { id: "robots", label: "robots.txt", icon: Shield },
    { id: "llms", label: "AI SEO (llms.txt)", icon: Bot },
    { id: "sitemap", label: "Sitemap", icon: MapPin },
  ];

  return (
    <AdminLayout title="SEO Management">
    <div className="space-y-6" data-testid="admin-seo">
      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === t.id ? "bg-[#0066CC] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`} data-testid={`seo-tab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Health Check Tab */}
      {activeTab === "health" && (
        <Card data-testid="seo-health-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#0066CC]" /> SEO Health Score</CardTitle>
                <CardDescription>Overall SEO readiness of your public pages</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadHealth} disabled={healthLoading}>
                <RefreshCw className={`w-4 h-4 mr-1.5 ${healthLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {healthLoading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : health ? (
              <div className="space-y-6">
                {/* Score Circle */}
                <div className="text-center">
                  <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full text-3xl font-bold ${
                    health.score >= 80 ? "bg-emerald-100 text-emerald-700" : health.score >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                  }`}>{health.score}</div>
                  <p className="text-slate-500 text-sm mt-2">{health.stats?.sitemap_urls || 0} pages in sitemap</p>
                </div>

                {/* Issues */}
                {health.issues?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-red-700 flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> Critical Issues</h4>
                    {health.issues.map((issue, i) => (
                      <div key={`issue-${i}-${issue.slice(0, 30)}`} className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{issue}</div>
                    ))}
                  </div>
                )}

                {/* Warnings */}
                {health.warnings?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-amber-700 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Warnings</h4>
                    {health.warnings.map((w, i) => (
                      <div key={`warn-${i}-${w.slice(0, 30)}`} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">{w}</div>
                    ))}
                  </div>
                )}

                {/* Good */}
                {health.good?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle className="w-4 h-4" /> Passing</h4>
                    {health.good.map((g, i) => (
                      <div key={`good-${i}-${g.slice(0, 30)}`} className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">{g}</div>
                    ))}
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "Verticals", value: health.stats?.verticals },
                    { label: "Problems", value: health.stats?.problems },
                    { label: "Pages with Meta", value: health.stats?.pages_with_meta },
                    { label: "Sitemap URLs", value: health.stats?.sitemap_urls },
                  ].map(s => (
                    <div key={s.label} className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-slate-800">{s.value}</p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">Click Refresh to run health check</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Page Meta Tab */}
      {activeTab === "pages" && (
        <Card data-testid="seo-pages-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#0066CC]" /> Page Meta Tags</CardTitle>
            <CardDescription>Configure title, description, and keywords for each public page. Vertical and problem pages auto-generate meta from their content.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-3 mb-2">
              <div>
                <p className="text-sm font-semibold text-slate-800">Bulk AI Generate Page Meta</p>
                <p className="text-[11px] text-slate-500">Generate keyword-rich titles, descriptions and keywords for all 9 public pages using AI.</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button onClick={() => runAIPageMetaGen(true)} disabled={aiMetaRunning} className="bg-[#0066CC] hover:bg-[#0052A3] gap-1.5" data-testid="ai-gen-pages-missing-btn">
                  {aiMetaRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Missing only
                </Button>
                <Button onClick={() => runAIPageMetaGen(false)} disabled={aiMetaRunning} variant="outline" className="gap-1.5" data-testid="ai-gen-pages-all-btn">
                  {aiMetaRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Regenerate all
                </Button>
              </div>
            </div>
            {PUBLIC_PAGES.map(page => {
              const meta = pageMetas[page.slug] || {};
              const isExpanded = expandedPage === page.slug;
              return (
                <div key={page.slug} className="border rounded-lg overflow-hidden">
                  <button onClick={() => setExpandedPage(isExpanded ? null : page.slug)}
                    className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors text-left"
                    data-testid={`seo-page-${page.slug}`}>
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <div>
                        <span className="text-sm font-medium text-slate-800">{page.label}</span>
                        <span className="text-xs text-slate-400 ml-2">{page.path}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {meta.title ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="p-4 border-t bg-slate-50/50 space-y-3">
                      <div>
                        <Label className="text-xs">Title Tag</Label>
                        <Input value={meta.title || ""} onChange={e => updatePageMeta(page.slug, "title", e.target.value)} placeholder={`${page.label} | Absolin`} className="text-sm" />
                        <p className="text-[10px] text-slate-400 mt-0.5">{(meta.title || "").length}/60 chars recommended</p>
                      </div>
                      <div>
                        <Label className="text-xs">Meta Description</Label>
                        <Textarea value={meta.description || ""} onChange={e => updatePageMeta(page.slug, "description", e.target.value)} placeholder="Describe this page in 150-160 characters..." rows={2} className="text-sm resize-none" />
                        <p className="text-[10px] text-slate-400 mt-0.5">{(meta.description || "").length}/160 chars recommended</p>
                      </div>
                      <div>
                        <Label className="text-xs">Keywords (comma-separated)</Label>
                        <Input value={meta.keywords || ""} onChange={e => updatePageMeta(page.slug, "keywords", e.target.value)} placeholder="ERP, digital transformation, Odoo..." className="text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">OG Image URL</Label>
                        <Input value={meta.og_image || ""} onChange={e => updatePageMeta(page.slug, "og_image", e.target.value)} placeholder="https://..." className="text-sm" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Switch checked={meta.noindex || false} onCheckedChange={v => updatePageMeta(page.slug, "noindex", v)} />
                          <Label className="text-xs text-slate-500">noindex (hide from search engines)</Label>
                        </div>
                        <Button size="sm" onClick={() => savePageMeta(page.slug)} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]">
                          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* robots.txt Tab */}
      {activeTab === "robots" && (
        <Card data-testid="seo-robots-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-[#0066CC]" /> robots.txt</CardTitle>
            <CardDescription>Control which pages search engines can crawl. Leave empty to use the auto-generated default.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea value={robotsTxt} onChange={e => setRobotsTxt(e.target.value)} rows={12} className="font-mono text-sm" placeholder="User-agent: *&#10;Allow: /&#10;Disallow: /admin-portal/&#10;..." />
            <div className="flex items-center justify-between">
              <a href="/api/robots.txt" target="_blank" rel="noopener noreferrer" className="text-sm text-[#0066CC] hover:underline flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" /> Preview Live robots.txt
              </a>
              <Button onClick={() => saveConfig("robots", robotsTxt)} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save robots.txt
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* llms.txt Tab */}
      {activeTab === "llms" && (
        <Card data-testid="seo-llms-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Bot className="h-5 w-5 text-[#0066CC]" /> AI SEO — llms.txt</CardTitle>
            <CardDescription>
              This file tells AI models (ChatGPT, Perplexity, Gemini) about your business. It helps your content appear in AI-generated answers.
              <a href="https://llmstxt.org" target="_blank" rel="noopener noreferrer" className="ml-1 text-[#0066CC] hover:underline inline-flex items-center gap-0.5">Learn more <ExternalLink className="w-3 h-3" /></a>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-1.5 mb-1"><Sparkles className="w-4 h-4" /> AI SEO Tips</h4>
              <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                <li>Include your core services, verticals, and unique value propositions</li>
                <li>Use natural language descriptions (AI models prefer readable content)</li>
                <li>List your free tools — AI models love referencing free resources</li>
                <li>Update whenever you add new services or verticals</li>
              </ul>
            </div>
            <Textarea value={llmsTxt} onChange={e => setLlmsTxt(e.target.value)} rows={16} className="font-mono text-sm" placeholder="# Your Company Name&#10;> Short description&#10;&#10;## About&#10;..." />
            <div className="flex items-center justify-between">
              <a href="/api/llms.txt" target="_blank" rel="noopener noreferrer" className="text-sm text-[#0066CC] hover:underline flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" /> Preview Live llms.txt
              </a>
              <Button onClick={() => saveConfig("llms_txt", llmsTxt)} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save llms.txt
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sitemap Tab */}
      {activeTab === "sitemap" && (
        <Card data-testid="seo-sitemap-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-[#0066CC]" /> Sitemap</CardTitle>
            <CardDescription>Auto-generated sitemap includes all public pages, verticals, and problems. Updated dynamically.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">sitemap.xml</p>
                  <p className="text-xs text-slate-500">Auto-generated from database — {health?.stats?.sitemap_urls || "..."} URLs</p>
                </div>
                <a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View Sitemap</Button>
                </a>
              </div>
              <div className="text-xs text-slate-500 space-y-1">
                <p>Includes:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>9 static public pages (homepage, verticals, assessment, etc.)</li>
                  <li>All {health?.stats?.verticals || "..."} vertical detail pages</li>
                  <li>All {health?.stats?.problems || "..."} problem detail pages</li>
                </ul>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Google Search Console</p>
                  <p className="text-xs text-slate-500">Submit your sitemap to Google for faster indexing</p>
                </div>
                <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm"><Search className="w-3.5 h-3.5 mr-1.5" /> Open Console</Button>
                </a>
              </div>
            </div>

            <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
              <h4 className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5 mb-1"><Sparkles className="w-4 h-4" /> SEO Best Practices</h4>
              <ul className="text-xs text-emerald-700 space-y-1 list-disc list-inside">
                <li>Submit sitemap URL to Google Search Console and Bing Webmaster Tools</li>
                <li>Ensure all page meta titles are unique and under 60 characters</li>
                <li>Meta descriptions should be 150-160 characters with keywords</li>
                <li>Update llms.txt whenever you add new verticals or services</li>
                <li>Monitor Google Core Web Vitals for page speed optimization</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Meta Tab */}
      {activeTab === "ai-meta" && (
        <Card data-testid="seo-ai-meta-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#0066CC]" /> AI-Generated SEO Meta</CardTitle>
            <CardDescription>Auto-generate keyword-rich titles &amp; descriptions for verticals and problem pages using AI. These overrides take precedence over default page titles.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Coverage stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">Verticals</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {entityMetas.vertical.length}
                  <span className="text-sm text-slate-500 font-normal"> / {health?.stats?.verticals || "..."}</span>
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{health?.stats?.vertical_ai_meta_coverage || 0}% covered</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">Problem Pages</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {entityMetas.problem.length}
                  <span className="text-sm text-slate-500 font-normal"> / {health?.stats?.problems || "..."}</span>
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{health?.stats?.problem_ai_meta_coverage || 0}% covered</p>
              </div>
            </div>

            {/* Generate buttons */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-800">Bulk Generate</p>
              <p className="text-xs text-slate-600">Uses Emergent LLM Key (gpt-4o). Generates only missing entities by default. Up to 500 per run. May take several minutes.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Button onClick={() => runAIMetaGen("vertical", true)} disabled={aiMetaRunning} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="ai-gen-verticals-btn">
                  {aiMetaRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />} Verticals (missing)
                </Button>
                <Button onClick={() => runAIMetaGen("problem", true)} disabled={aiMetaRunning} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="ai-gen-problems-btn">
                  {aiMetaRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />} Problems (missing)
                </Button>
                <Button onClick={() => runAIMetaGen("both", false)} disabled={aiMetaRunning} variant="outline" data-testid="ai-gen-all-btn">
                  {aiMetaRunning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Regenerate ALL
                </Button>
              </div>
              {aiMetaResult && (
                <div className="text-xs bg-white border border-slate-200 rounded p-3 mt-2" data-testid="ai-gen-result">
                  <p><span className="font-semibold text-emerald-700">{aiMetaResult.generated}</span> generated · <span className="font-semibold text-red-600">{aiMetaResult.failed}</span> failed · <span className="font-semibold">{aiMetaResult.total_attempted}</span> attempted</p>
                  {aiMetaResult.errors_sample?.length > 0 && (
                    <div className="mt-1 text-red-600">
                      Errors: <ul className="list-disc list-inside">{aiMetaResult.errors_sample.map((e, i) => <li key={i}>{e}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Entity meta table */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <button onClick={() => setEntityMetaFilter("vertical")} className={`text-xs px-3 py-1.5 rounded-full ${entityMetaFilter === "vertical" ? "bg-[#0066CC] text-white" : "bg-slate-100 text-slate-600"}`}>Verticals ({entityMetas.vertical.length})</button>
                <button onClick={() => setEntityMetaFilter("problem")} className={`text-xs px-3 py-1.5 rounded-full ${entityMetaFilter === "problem" ? "bg-[#0066CC] text-white" : "bg-slate-100 text-slate-600"}`}>Problems ({entityMetas.problem.length})</button>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="ai-meta-list">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs">
                    <tr>
                      <th className="text-left p-2 font-semibold text-slate-600">Title</th>
                      <th className="text-left p-2 font-semibold text-slate-600 hidden md:table-cell">Description</th>
                      <th className="text-left p-2 font-semibold text-slate-600 hidden md:table-cell w-[140px]">Focus Keyword</th>
                      <th className="text-left p-2 font-semibold text-slate-600 w-[60px]">AI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(entityMetas[entityMetaFilter] || []).slice(0, 50).map((m) => (
                      <tr key={m.entity_id} className="border-t border-slate-100 align-top">
                        <td className="p-2 text-slate-800">
                          <div className="line-clamp-2">{m.title || <span className="italic text-slate-400">— no title —</span>}</div>
                          <a href={`/${entityMetaFilter}/${m.entity_id}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#0066CC] inline-flex items-center gap-0.5 mt-1"><ExternalLink className="w-3 h-3" /> view</a>
                        </td>
                        <td className="p-2 text-slate-600 hidden md:table-cell"><div className="line-clamp-2 max-w-md">{m.description}</div></td>
                        <td className="p-2 text-slate-600 hidden md:table-cell text-xs">{m.focus_keyword}</td>
                        <td className="p-2">{m.ai_generated ? <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">AI</span> : <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">manual</span>}</td>
                      </tr>
                    ))}
                    {(entityMetas[entityMetaFilter] || []).length === 0 && (
                      <tr><td colSpan={4} className="p-6 text-center text-xs text-slate-400">No SEO meta yet — click a "Generate" button above to start.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {(entityMetas[entityMetaFilter] || []).length > 50 && (
                <p className="text-xs text-slate-500">Showing first 50 of {entityMetas[entityMetaFilter].length} — use admin DB tools for full export.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Integrations Tab */}
      {activeTab === "integrations" && (
        <Card data-testid="seo-integrations-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-[#0066CC]" /> Tracking &amp; Verification Integrations</CardTitle>
            <CardDescription>Inject Google Analytics 4, Google Tag Manager, Google Search Console verification, and Microsoft Clarity tags into every public page&apos;s &lt;head&gt;.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="ga4">Google Analytics 4 — Measurement ID</Label>
              <Input id="ga4" placeholder="G-XXXXXXXXXX" value={integrations.ga4_id} onChange={e => setIntegrations(p => ({ ...p, ga4_id: e.target.value.trim() }))} data-testid="ga4-input" />
              <p className="text-[11px] text-slate-500">Find at <a className="text-[#0066CC]" href="https://analytics.google.com" target="_blank" rel="noopener noreferrer">analytics.google.com</a> → Admin → Data Streams → Web → Measurement ID.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gsc">Google Search Console — Verification Code</Label>
              <Input id="gsc" placeholder="abc123XYZ_xyz456..." value={integrations.gsc_verification} onChange={e => setIntegrations(p => ({ ...p, gsc_verification: e.target.value.trim() }))} data-testid="gsc-input" />
              <p className="text-[11px] text-slate-500">Get from <a className="text-[#0066CC]" href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">Search Console</a> → Settings → Ownership verification → HTML tag → copy the value of <code>content="..."</code>.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gtm">Google Tag Manager — Container ID (optional)</Label>
              <Input id="gtm" placeholder="GTM-XXXXXXX" value={integrations.gtm_id} onChange={e => setIntegrations(p => ({ ...p, gtm_id: e.target.value.trim() }))} data-testid="gtm-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="clarity">Microsoft Clarity — Project ID (optional)</Label>
              <Input id="clarity" placeholder="abcd1234ef" value={integrations.ms_clarity_id} onChange={e => setIntegrations(p => ({ ...p, ms_clarity_id: e.target.value.trim() }))} data-testid="clarity-input" />
              <p className="text-[11px] text-slate-500">Free heatmaps + session replay at <a className="text-[#0066CC]" href="https://clarity.microsoft.com" target="_blank" rel="noopener noreferrer">clarity.microsoft.com</a>.</p>
            </div>
            <Button onClick={saveIntegrations} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-integrations-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Integrations
            </Button>
            <p className="text-[11px] text-slate-500">After saving, do a full page reload (Ctrl+F5) on a public page to confirm the tags appear in <code>&lt;head&gt;</code>.</p>
          </CardContent>
        </Card>
      )}
    </div>
    </AdminLayout>
  );
}
